/**
 * What is left of an emplacement that has been dealt with (ROUND4 §3). Generic rubble said "something
 * was here"; these say WHICH thing, and say it loudly - a bunker cracked open with its roof slab in the
 * chamber, a turret blown off its drum, a nest of burst bags with the gun on its side. A player has to
 * be able to tell a wrecked gun position from a quiet one across the width of the screen.
 */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { debris, scorch } from './wreckParts';

const TAU = Math.PI * 2;

/** Bunker: the south wall blown in either side of the embrasure, the roof slab cracked and fallen inside. */
export const bunkerWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 6.0, 4.8);
  const broken = { all: p.color('concrete.lo'), top: p.color('ink.1'), front: p.color('concrete.mid') };
  for (const sx of [-1, 1]) mb.box(1.5, rng.range(0.7, 1.2), 0.9, broken, place(sx * 1.9, 0, 1.55));
  mb.box(5.2, rng.range(0.9, 1.2), 0.9, broken, place(0, 0, -1.55));
  for (const sx of [-1, 1]) mb.box(0.9, rng.range(0.9, 1.35), 3.2, broken, place(sx * 2.15, 0, 0));
  const slab = { all: p.color('concrete.mid'), top: p.color('concrete.hi') };
  mb.box(2.9, 0.4, 3.2, slab, place(-1.1, 0.2, 0.2, 0.16, 1, 0.2, -0.1));
  mb.box(2.4, 0.4, 2.6, { ...slab, top: p.color('ink.1') }, place(1.5, 0.1, -0.2, -0.2, 1, -0.12, 0.18));
  debris(mb, p, rng, 5, 2.5, 1.9);
  return mb;
};

/** Turret bunker: the drum cracked down to a stump, the steel turret off its ring and lying beside it. */
export const turretBunkerWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const flat = Math.PI / 10;
  scorch(mb, p, 4.5, 4.5);
  mb.cylinder(2.15, 2.0, 0.8, 10, p.color('concrete.lo'), place(0, 0, 0, flat), p.color('ink.1'));
  // The one piece of the roof ring still hanging on, tipped over the broken drum.
  const ring = { all: p.color('concrete.mid'), top: p.color('ink.1') };
  mb.box(2.3, 0.3, 1.3, ring, place(-0.55, 0.7, -0.85, 0.3, 1, 0.16));
  // Turret on its side, the barrel bent into the ground where it fell.
  mb.cylinder(1.05, 0.6, 0.62, 8, p.color('ink.1'), place(1.3, 0.12, 0.95, 0.5, 1, 1.3), p.color('metal.lo'));
  mb.box(0.2, 0.2, 1.2, p.color('ink.1'), place(1.15, 0.18, 1.55, rng.range(0.3, 0.9), 1, 0.3));
  debris(mb, p, rng, 5, 1.9, 1.9);
  return mb;
};

/** MG nest: the horseshoe burst open, sand spilt over the pad, the gun tipped off its tripod. */
export const mgNestWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  // The burn sits over the sim's three collider boxes (z -0.7 to 1.9), not over the open end behind it.
  scorch(mb, p, 3.6, 2.75, 0, 0.53);
  const burst = { all: p.color('canvas.lo'), top: p.color('ink.1') };
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI * 0.62 + (i / 6) * Math.PI * 1.24;
    const at = place(Math.sin(a) * 1.7, 0, Math.cos(a) * 1.7, a, 1, 0, rng.range(-0.25, 0.25));
    mb.box(0.78, rng.range(0.12, 0.34), 0.42, burst, at);
  }
  // Bags thrown clear, flat on the ground inside the ring.
  for (let i = 0; i < 4; i++) {
    const at = place(rng.range(-1.5, 1.5), 0, rng.range(-0.3, 1.3), rng.range(0, TAU), 1, rng.range(-0.15, 0.15));
    mb.box(rng.range(0.5, 0.8), 0.18, 0.4, burst, at);
  }
  mb.box(0.12, 0.12, 1.2, p.color('ink.1'), place(0.3, 0.1, 0.7, 0.9, 1, 0.12));
  mb.box(0.3, 0.18, 0.3, p.color('ink.1'), place(0.05, 0, 0.25, 0.4));
  return mb;
};
